import React, { useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { useSiteConfig, pickI18n } from '../contexts/SiteConfigContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const Contact = ({ hideHeader = false }) => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  const colors = config.identity?.colors || {};
  const contact = config.contact || {};

  const [form, setForm] = useState({
    name: '', email: '', phone: '', service: '', date: '', message: '',
  });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const labels = {
    de: { name: 'Name', email: 'E-Mail', phone: 'Telefon', service: 'Service auswählen', date: 'Wunschtermin', message: 'Nachricht', submit: 'Nachricht senden', success: 'Vielen Dank! Wir melden uns bald.', addressTitle: 'Adresse', phoneTitle: 'Telefon', emailTitle: 'E-Mail', hoursTitle: 'Öffnungszeiten' },
    en: { name: 'Name', email: 'Email', phone: 'Phone', service: 'Select Service', date: 'Preferred Date', message: 'Message', submit: 'Send Message', success: 'Thank you! We will get back to you soon.', addressTitle: 'Address', phoneTitle: 'Phone', emailTitle: 'Email', hoursTitle: 'Opening Hours' },
    ar: { name: 'الاسم', email: 'البريد الإلكتروني', phone: 'الهاتف', service: 'اختاري الخدمة', date: 'الموعد المفضل', message: 'الرسالة', submit: 'إرسال الرسالة', success: 'شكراً لكِ! سنتواصل معكِ قريباً.', addressTitle: 'العنوان', phoneTitle: 'الهاتف', emailTitle: 'البريد الإلكتروني', hoursTitle: 'ساعات العمل' },
  };
  const L = labels[lang] || labels.de;

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/messages', form);
      toast.success(L.success);
      setForm({ name: '', email: '', phone: '', service: '', date: '', message: '' });
    } catch {
      toast.error('Error sending message');
    }
  };

  const infos = [
    { icon: MapPin, title: L.addressTitle, value: contact.address },
    { icon: Phone, title: L.phoneTitle, value: contact.phone },
    { icon: Mail, title: L.emailTitle, value: contact.email },
    { icon: Clock, title: L.hoursTitle, value: contact.hours },
  ].filter((x) => x.value);

  const tag = lang === 'de' ? 'Kontakt' : lang === 'ar' ? 'اتصل بنا' : 'Contact';
  const title = lang === 'de' ? 'Wir freuen uns auf Sie' : lang === 'ar' ? 'نتطلع للقائك' : 'We Look Forward to Meeting You';

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {!hideHeader && (
          <div className="text-center mb-16">
            <p
              className="text-xs md:text-sm mb-4 tracking-[0.3em] uppercase font-medium"
              style={{ color: colors.accent || '#b54e7d' }}
            >
              {tag}
            </p>
            <h2
              className="font-display mb-4"
              style={{
                color: colors.text_dark || '#5a1d3b',
                fontSize: 'clamp(1.875rem, 3vw, 2.75rem)',
                fontWeight: 500,
              }}
            >
              {title}
            </h2>
            <div
              className="w-16 h-px mx-auto mt-8 opacity-40"
              style={{ backgroundColor: colors.primary || '#c4477a' }}
            />
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="lg:col-span-2 space-y-4">
            {infos.map((info, i) => {
              const Icon = info.icon;
              return (
                <div key={i} className="flex gap-4 bg-white rounded-2xl p-6 border border-pink-100">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: colors.bg_light || '#fdf2f5' }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: colors.primary || '#c4477a' }} />
                  </div>
                  <div className="min-w-0">
                    <div
                      className="font-medium mb-1 text-[15px]"
                      style={{ color: colors.text_dark || '#5a1d3b' }}
                    >
                      {info.title}
                    </div>
                    <div className="text-gray-600 text-sm whitespace-pre-line break-words leading-relaxed">
                      {info.value}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 space-y-5"
            style={{ boxShadow: '0 1px 3px rgba(196, 71, 122, 0.06), 0 8px 24px -10px rgba(196, 71, 122, 0.12)' }}
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.name}</Label>
                <Input required value={form.name} onChange={onChange('name')} className="h-11" />
              </div>
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.email}</Label>
                <Input required type="email" value={form.email} onChange={onChange('email')} className="h-11" />
              </div>
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.phone}</Label>
                <Input value={form.phone} onChange={onChange('phone')} className="h-11" />
              </div>
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.date}</Label>
                <Input type="date" value={form.date} onChange={onChange('date')} className="h-11" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.service}</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder={L.service} />
                </SelectTrigger>
                <SelectContent>
                  {(config.services || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {pickI18n(s.name, lang)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.message}</Label>
              <Textarea rows={4} value={form.message} onChange={onChange('message')} />
            </div>
            <Button
              type="submit"
              className="btn-rose text-white rounded-full px-8 h-12 gap-2 w-full md:w-auto font-medium"
              style={{ backgroundColor: colors.primary || '#c4477a' }}
            >
              <Send className="w-4 h-4 rtl-flip" />
              {L.submit}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
